<template>
    <div class="student-roadmap" data-testid="student-roadmap">
        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <p v-if="loading" data-testid="student-roadmap-loading">Loading...</p>

        <div v-if="!loading">
            <div v-if="requirements.length === 0" class="alert alert-info inline" data-testid="student-roadmap-empty">
                This student does not have a Roadmap assigned.
            </div>

            <div v-if="requirements.length > 0">
                <p class="lead" data-testid="student-roadmap-progress">
                    <strong>{{ roadmapName }}</strong> — {{ completedCount }} of
                    {{ requirements.length }} lessons completed<span v-if="currentLesson">,
                    currently on Lesson {{ currentLesson }}</span>
                </p>

                <table class="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>Lesson</th>
                            <th>Session Type</th>
                            <th>Instructor</th>
                            <th>Report Card</th>
                            <th>Document</th>
                            <th>Skills</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="requirement in requirements"
                            :key="requirement.id"
                            data-testid="student-roadmap-lesson"
                        >
                            <td>{{ requirement.lesson_number }}</td>
                            <td>{{ requirement.sessionType ? requirement.sessionType.name : '' }}</td>
                            <td>{{ instructorName(requirement) }}</td>
                            <td>{{ requirement.reportcardType ? requirement.reportcardType.name : 'Default' }}</td>
                            <td>{{ requirement.documentTemplate ? requirement.documentTemplate.name : '—' }}</td>
                            <td>{{ skillsSummary(requirement) }}</td>
                            <td>
                                <span v-if="requirement.completed_at" class="label label-success">Completed</span>
                                <span v-else-if="requirement.drive_id" class="label label-info">
                                    Scheduled{{ driveDate(requirement) }}
                                </span>
                                <span v-else class="label label-default">Upcoming</span>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <button
                    class="btn btn-dc btn-red"
                    data-testid="clear-roadmap"
                    :disabled="assigning"
                    @click="clearRoadmap"
                >
                    Clear Roadmap
                </button>
                <p class="help-block">
                    Clearing removes the lessons the student hasn't completed.
                    Completed lessons are kept as history.
                </p>
                <hr />
            </div>

            <div class="form-inline" style="margin-top: 1em">
                <div class="form-group">
                    <label for="assign-roadmap" style="margin-right: 0.5em">
                        {{ requirements.length > 0 ? 'Replace with' : 'Assign a Roadmap' }}
                    </label>
                    <select
                        id="assign-roadmap"
                        v-model="selectedRoadmapId"
                        class="form-control"
                        data-testid="assign-roadmap-select"
                    >
                        <option :value="null">Choose a roadmap...</option>
                        <option v-for="roadmap in roadmaps" :key="roadmap.id" :value="roadmap.id">
                            {{ roadmap.name }}
                        </option>
                    </select>
                </div>
                <button
                    class="btn btn-dc"
                    style="margin-left: 0.5em"
                    data-testid="assign-roadmap"
                    :disabled="assigning || selectedRoadmapId === null"
                    @click="assignRoadmap"
                >
                    {{ assigning ? 'Assigning...' : 'Assign' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'StudentRoadmapPanel',
    props: {
        requirements: {
            type: Array,
            default: () => []
        },
        currentLesson: {
            type: Number,
            default: null
        },
        roadmaps: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        assigning: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ''
        },
        onAssign: {
            type: Function,
            required: true
        },
        onClear: {
            type: Function,
            required: true
        }
    },
    data() {
        return {
            selectedRoadmapId: null
        };
    },
    computed: {
        completedCount() {
            return this.requirements.filter((requirement) => requirement.completed_at).length;
        },
        roadmapName() {
            const withRoadmap = this.requirements.find((requirement) => requirement.roadmap);
            return withRoadmap ? withRoadmap.roadmap.name : 'Roadmap';
        }
    },
    methods: {
        instructorName(requirement) {
            if (!requirement.instructor) {
                return 'Any instructor';
            }
            return [requirement.instructor.firstname, requirement.instructor.lastname]
                .filter(Boolean)
                .join(' ');
        },
        skillsSummary(requirement) {
            const taskIds = requirement.task_ids || [];
            return taskIds.length > 0 ? taskIds.length + ' selected' : 'Full list';
        },
        driveDate(requirement) {
            return requirement.drive && requirement.drive.drive_date
                ? ' for ' + requirement.drive.drive_date
                : '';
        },
        assignRoadmap() {
            this.onAssign(this.selectedRoadmapId);
        },
        clearRoadmap() {
            this.onClear();
        }
    }
};
</script>
